/**
 * Genius Tracker Apps — i18n foundation.
 *
 * Goals (docs/foundation/DESIGN_SYSTEM_RULES.md §6, PRODUCT_VISION.md §4):
 *
 * - AR, DE, EN, FR supported from day one.
 * - Arabic is first-class, RTL is not retrofitted.
 * - The frontend is direction-aware from the root: `dir` flips via the
 *   `LocaleProvider`, and layouts use logical CSS (block/inline).
 * - Translation catalogues are authored under `catalogs/<locale>.json`
 *   and are the single source of truth. Missing keys fall back to the
 *   key itself so screens never break.
 */

export const SUPPORTED_LOCALES = ["en", "de", "fr", "ar"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const RTL_LOCALES = new Set<Locale>(["ar"]);

export function getDirection(locale: Locale): "ltr" | "rtl" {
  return RTL_LOCALES.has(locale) ? "rtl" : "ltr";
}

export interface Catalog {
  readonly [key: string]: string;
}

export function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  let raw = template;
  // GT-GFI-AI-POSTDEPLOY-POLISH-AND-SAFETY-FIXES-01 (DL-118) — minimal
  // ICU-lite pluralisation. A catalog value MAY encode two forms separated by
  // a single pipe: `"<one>|<other>"`. When the caller passes a numeric `count`,
  // select the singular form for `count === 1` and the plural form otherwise,
  // THEN interpolate. Without a `count` var (or without a pipe) the value is
  // used verbatim, so non-plural values are unaffected. This fixes the
  // concatenated `Based on 1 cited sourceBased on N cited sources` footer.
  if (typeof vars.count === "number" && raw.includes("|")) {
    const sep = raw.indexOf("|");
    raw = vars.count === 1 ? raw.slice(0, sep) : raw.slice(sep + 1);
  }
  return raw.replace(/\{(\w+)\}/g, (_, k) =>
    k in vars ? String(vars[k as keyof typeof vars]) : `{${k}}`,
  );
}

/**
 * F-PLAN-NAMEN1 (Owner-Entscheidung 2026-10-09) — the internal billing plan
 * keys (`services/api/src/modules/billing-modules/plan-mapping.ts`) never
 * change, but every human-facing render of a plan uses the brand name
 * instead of the raw key. `planNameKey` is the one place that maps a key to
 * its `plan.name.*` catalog entry, so no component interpolates the raw
 * key directly.
 */
export const PLAN_IDS = ["free", "starter", "pro", "enterprise"] as const;
export type PlanId = (typeof PLAN_IDS)[number];

export function planNameKey(plan: PlanId): string {
  return `plan.name.${plan}`;
}

export function translator(catalog: Catalog) {
  return function t(key: string, vars?: Record<string, string | number>): string {
    const raw = catalog[key];
    return interpolate(raw ?? key, vars);
  };
}

/**
 * Best-effort negotiation of the browser's preferred locale against
 * our supported list. Accepts BCP-47 tags ("en-US", "ar-EG", …).
 */
export function negotiateLocale(candidates: readonly string[], fallback: Locale = "en"): Locale {
  for (const c of candidates) {
    const base = c.toLowerCase().split("-")[0];
    if (!base) continue;
    const match = SUPPORTED_LOCALES.find((l) => l === base);
    if (match) return match;
  }
  return fallback;
}

/**
 * The locale the app is currently rendering in, for callers that are not
 * React components.
 *
 * `LocaleProvider` writes the active locale onto `<html lang>` on every
 * change, so the document element already carries it. Reading it back here
 * keeps pure helpers (formatters, sorters) locale-true without a second
 * store to keep in sync and without turning them into hooks. Outside a
 * browser — SSR, node-environment tests — there is no document and the
 * fallback applies.
 */
export function activeLocale(fallback: Locale = "en"): Locale {
  // Reached through `globalThis`, not as a bare `document`: this module is also
  // compiled by server packages whose tsconfig has no DOM lib, and there a bare
  // reference is a type error rather than the runtime-guarded branch it is here.
  const doc = (globalThis as { document?: { documentElement?: { lang?: string } } }).document;
  if (!doc) return fallback;
  return negotiateLocale([doc.documentElement?.lang ?? ""], fallback);
}
